import React, { useState, useEffect } from 'react';
import { AlertCircle, CheckCircle, XCircle, Trophy, RefreshCw, BookOpen, Sparkles, Brain, Microscope, Clock, Shield, Activity, Zap, Users, ArrowRight } from 'lucide-react';

const ImmunoDx = () => {
  const [currentCase, setCurrentCase] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [showExplanation, setShowExplanation] = useState(false);
  const [score, setScore] = useState(0);
  const [gameComplete, setGameComplete] = useState(false);
  const [mode, setMode] = useState('menu'); // menu, learn, play, reverse
  const [learnSection, setLearnSection] = useState('overview');
  const [activeQuestions, setActiveQuestions] = useState([]);

  // --- DATA: 40 DETAILED CLINICAL CASES (FORWARD) ---
  const casesData = [
    // --- B-CELL DISORDERS ---
    {
      id: 1,
      title: "Case: The Boy with Small Tonsils",
      age: "9-month-old male",
      presentation: "A mother brings her 9-month-old son to the pediatrician for his fourth episode of high fever and ear tugging in the last 3 months. He was a healthy full-term infant and breastfed well, but starting around 6 months of age, he developed recurrent bacterial otitis media and one episode of pneumococcal pneumonia.",
      physicalExam: "Exam reveals a well-developed infant with scarred tympanic membranes. Notably, the tonsils are virtually absent upon oral inspection, and there are no palpable cervical lymph nodes despite the history of infection.",
      labs: "Serum studies show markedly decreased IgG, IgA, and IgM levels (< 2 SD below mean). Flow cytometry demonstrates a complete absence of CD19+ B cells in the peripheral blood.",
      buzzwords: ["Absent tonsils", "Infections start >6mo", "Male patient", "Absent B cells"],
      options: [
        { text: "Bruton's X-linked Agammaglobulinemia", correct: true },
        { text: "Common Variable Immunodeficiency (CVID)", correct: false },
        { text: "Severe Combined Immunodeficiency (SCID)", correct: false },
        { text: "Hyper-IgM Syndrome", correct: false }
      ],
      explanation: "This is the classic presentation of X-Linked Agammaglobulinemia (XLA). Maternal IgG protects the infant for the first ~6 months. Once that wanes, the inability to produce B cells (due to BTK mutation) leads to recurrent infections with encapsulated bacteria. The absence of tonsils and lymph nodes is a key physical finding distinguishing it from other B-cell defects.",
      defect: "BTK tyrosine kinase mutation → B-cell maturation arrest",
      infections: "Encapsulated bacteria (S. pneumo, H. flu), Enteroviruses",
      treatment: "Lifelong IVIG replacement"
    },
    {
      id: 2,
      title: "Case: Recurrent Pneumonia in a Toddler",
      age: "2-year-old male",
      presentation: "A 2-year-old male is admitted to the hospital for his third confirmed episode of bacterial pneumonia this year. His parents report that he takes longer to recover from colds than his siblings and has had multiple courses of antibiotics for sinus infections.",
      physicalExam: "Lung exam reveals crackles in the right lower lobe. The child has scant lymphoid tissue; lymph nodes are not palpable in the neck or axilla.",
      labs: "CBC shows normal neutrophil and lymphocyte counts. However, flow cytometry reveals <1% B cells. Quantitative immunoglobulins show pan-hypogammaglobulinemia (low IgG, IgM, IgA).",
      buzzwords: ["X-linked pattern", "Recurrent pneumonia", "Zero B cells"],
      options: [
        { text: "Bruton's X-linked Agammaglobulinemia", correct: true },
        { text: "Selective IgA Deficiency", correct: false },
        { text: "Leukocyte Adhesion Deficiency", correct: false },
        { text: "Chronic Granulomatous Disease", correct: false }
      ],
      explanation: "This is another presentation of XLA. The key differentiator from CVID is the age of onset (very early) and the COMPLETE absence of B cells on flow cytometry. CVID patients usually have B cells, they just don't work.",
      defect: "BTK mutation",
      infections: "Encapsulated bacteria, Giardia",
      treatment: "IVIG and aggressive antibiotic therapy"
    },
    {
      id: 3,
      title: "Case: The 'Healthy' Blood Donor",
      age: "24-year-old male",
      presentation: "A 24-year-old medical student volunteers to donate blood for the first time. He feels entirely well and has no significant medical history, though he mentions he gets occasional episodes of loose stools and bloating that he attributes to lactose intolerance.",
      physicalExam: "Unremarkable. Vital signs are stable.",
      labs: "The blood bank notifies him that his donation was flagged. Follow-up testing shows serum IgA < 7 mg/dL (undetectable). IgG and IgM levels are within normal limits.",
      buzzwords: ["Asymptomatic", "Most common immunodeficiency", "Giardia risk", "Blood donor"],
      options: [
        { text: "Selective IgA Deficiency", correct: true },
        { text: "CVID", correct: false },
        { text: "Hyper-IgM Syndrome", correct: false },
        { text: "X-Linked Agammaglobulinemia", correct: false }
      ],
      explanation: "Selective IgA Deficiency is the most common primary immunodeficiency. Most patients are asymptomatic, but some experience recurrent sinopulmonary infections or GI issues (like Giardia, causing his bloating).",
      defect: "Failure of B cells to class switch to IgA",
      infections: "Giardia lamblia, mild respiratory infections",
      treatment: "Observation; treat infections as needed"
    },
    {
      id: 4,
      title: "Case: Anaphylaxis after Transfusion",
      age: "30-year-old female",
      presentation: "A 30-year-old female is involved in a motor vehicle accident and requires a blood transfusion. Within seconds of starting the packed red blood cells, she develops difficulty breathing, hives, and hypotension.",
      physicalExam: "Patient is wheezing and hypotensive. Skin exam shows diffuse urticaria.",
      labs: "Stabilization labs reveal IgA levels are undetectable. Further testing confirms high titers of anti-IgA IgE antibodies.",
      buzzwords: ["Anaphylaxis to blood products", "Anti-IgA antibodies", "Medical emergency"],
      options: [
        { text: "Selective IgA Deficiency", correct: true },
        { text: "CVID", correct: false },
        { text: "Complement Deficiency", correct: false },
        { text: "SCID", correct: false }
      ],
      explanation: "Patients with severe Selective IgA deficiency can form IgE antibodies against the small amount of IgA present in donor blood. This leads to immediate anaphylaxis upon transfusion.",
      defect: "Severe IgA deficiency with anti-IgA antibodies",
      infections: "Variable (often mild)",
      treatment: "Must receive washed RBCs or IgA-deficient blood products"
    },
    {
      id: 5,
      title: "Case: College Student with Sinusitis",
      age: "22-year-old female",
      presentation: "A 22-year-old college student presents to the student health center complaining of her fifth sinus infection this year. She reports a history of 'weak lungs' and bronchitis throughout high school but was healthy as a young child.",
      physicalExam: "Nasal mucosal swelling and purulent discharge. Tonsils are present. Mild splenomegaly is noted.",
      labs: "Quantitative immunoglobulins reveal low IgG (<400) and low IgA. IgM is borderline low. Flow cytometry shows a normal number of B cells, but vaccine titers show poor response to Pneumovax.",
      buzzwords: ["Adult onset", "Normal B cell count", "Low Immunoglobulins", "Poor vaccine response"],
      options: [
        { text: "Common Variable Immunodeficiency (CVID)", correct: true },
        { text: "X-Linked Agammaglobulinemia", correct: false },
        { text: "SCID", correct: false },
        { text: "Wiskott-Aldrich Syndrome", correct: false }
      ],
      explanation: "CVID typically presents in late adolescence or young adulthood (20s-30s). Unlike XLA, B cells are present in normal numbers, but they fail to differentiate into plasma cells, leading to low antibodies and poor vaccine response.",
      defect: "Defective B-cell differentiation to plasma cells",
      infections: "Encapsulated bacteria, Mycoplasma",
      treatment: "IVIG replacement"
    },
    {
      id: 6,
      title: "Case: Anemia and Pneumonia",
      age: "35-year-old male",
      presentation: "A 35-year-old male presents with fatigue and yellowing of the eyes. He gives a history of being hospitalized twice in the last 5 years for pneumonia. He also reports chronic diarrhea.",
      physicalExam: "Scleral icterus is present. Abdominal exam reveals a palpable spleen tip (splenomegaly).",
      labs: "CBC shows hemolytic anemia (positive Coombs test) and thrombocytopenia. Immunoglobulin levels are depressed across all classes (IgG, IgA, IgM).",
      buzzwords: ["Autoimmunity (ITP/Hemolytic anemia)", "Splenomegaly", "Hypogammaglobulinemia"],
      options: [
        { text: "Common Variable Immunodeficiency (CVID)", correct: true },
        { text: "X-Linked Agammaglobulinemia", correct: false },
        { text: "Chronic Granulomatous Disease", correct: false },
        { text: "Leukocyte Adhesion Deficiency", correct: false }
      ],
      explanation: "CVID has a strong association with autoimmune conditions (like Autoimmune Hemolytic Anemia and ITP) and lymphoproliferation (splenomegaly, granulomas). This 'immune dysregulation' is a hallmark differentiating it from simple XLA.",
      defect: "B-cell differentiation defect",
      infections: "Bacteria + Autoimmune complications",
      treatment: "IVIG + immunosuppression for autoimmunity"
    },
    
    // --- T-CELL DISORDERS ---
    {
      id: 7,
      title: "Case: Newborn with Heart Murmur",
      age: "2-day-old male",
      presentation: "A newborn male is evaluated in the nursery for cyanosis. An echocardiogram reveals Truncus Arteriosus. The nursing staff notes the baby is jittery and has had subtle twitching of the extremities.",
      physicalExam: "Dysmorphic features include low-set ears, a short philtrum, and micrognathia. A harsh systolic murmur is audible.",
      labs: "Serum calcium is critically low (6.5 mg/dL). FISH analysis confirms a deletion on chromosome 22q11.2.",
      buzzwords: ["Truncus arteriosus", "Hypocalcemia", "22q11", "CATCH-22"],
      options: [
        { text: "Thymic Aplasia (DiGeorge Syndrome)", correct: true },
        { text: "Severe Combined Immunodeficiency (SCID)", correct: false },
        { text: "Wiskott-Aldrich Syndrome", correct: false },
        { text: "Ataxia-Telangiectasia", correct: false }
      ],
      explanation: "This is the classic CATCH-22 presentation of DiGeorge Syndrome: Cardiac defects (Truncus/Tetralogy), Abnormal facies, Thymic hypoplasia, Cleft palate, Hypocalcemia. The twitching is due to hypocalcemic tetany.",
      defect: "Maldevelopment of 3rd/4th pharyngeal pouches",
      infections: "Viral/Fungal (severity depends on thymus size)",
      treatment: "Calcium correction, Thymus transplant (if severe)"
    },
    {
      id: 8,
      title: "Case: Seizures in a Newborn",
      age: "1-week-old female",
      presentation: "A 1-week-old female is brought to the ED for generalized tonic-clonic seizures. She has been feeding poorly. There is no history of fever.",
      physicalExam: "Examination reveals a submucous cleft palate. Chest X-ray performed during the workup shows an absent thymic shadow.",
      labs: "Serum calcium is 5.8 mg/dL. PTH levels are undetectable. Lymphocyte count shows low T-cells.",
      buzzwords: ["Absent thymic shadow", "Hypocalcemic seizures", "Cleft palate"],
      options: [
        { text: "Thymic Aplasia (DiGeorge Syndrome)", correct: true },
        { text: "Bruton's XLA", correct: false },
        { text: "Chronic Mucocutaneous Candidiasis", correct: false },
        { text: "Job Syndrome", correct: false }
      ],
      explanation: "Hypocalcemic seizures in a newborn with an absent thymus are highly specific for DiGeorge Syndrome. Parathyroid hypoplasia leads to the low calcium.",
      defect: "22q11.2 Deletion",
      infections: "Variable T-cell deficiency",
      treatment: "Calcium/Vit D supplementation"
    },
    {
      id: 9,
      title: "Case: Disseminated BCG",
      age: "4-year-old male",
      presentation: "A 4-year-old male recently immigrated to the US. He received the BCG vaccine in his home country 3 months ago and now presents with a swollen, ulcerated axillary lymph node and fever. Blood cultures are positive for Mycobacterium bovis.",
      physicalExam: "Generalized lymphadenopathy and hepatosplenomegaly.",
      labs: "Immunoglobulin levels are normal. T-cell and B-cell counts are normal. However, his white blood cells show reduced IFN-gamma production in response to IL-12 stimulation.",
      buzzwords: ["Mycobacteria", "Salmonella", "BCG reaction", "IL-12/IFN-gamma axis"],
      options: [
        { text: "IL-12 Receptor Deficiency", correct: true },
        { text: "Chronic Granulomatous Disease", correct: false },
        { text: "Hyper-IgM Syndrome", correct: false },
        { text: "Leukocyte Adhesion Deficiency", correct: false }
      ],
      explanation: "This patient has a defect in the IL-12/IFN-gamma pathway (Mendelian Susceptibility to Mycobacterial Disease). IL-12 signals Th1 cells to release IFN-gamma, which activates macrophages. Without this, macrophages cannot kill intracellular pathogens like TB, BCG, or Salmonella.",
      defect: "IL-12 Receptor mutation",
      infections: "Mycobacteria, Salmonella",
      treatment: "Antibiotics, IFN-gamma (if applicable)"
    },
    {
      id: 10,
      title: "Case: The Cold Abscess",
      age: "6-year-old female",
      presentation: "A 6-year-old female presents with a large, fluctuant mass on her back. Despite the size of the abscess, the area is not warm, red, or tender ('cold abscess'). She has a history of severe eczema since infancy.",
      physicalExam: "The patient has coarse facial features (broad nose, deep-set eyes). There is widespread eczematous rash.",
      labs: "Serum IgE is > 2000 IU/mL. CBC shows significant eosinophilia.",
      buzzwords: ["Cold abscess", "Coarse facies", "High IgE", "Eczema"],
      options: [
        { text: "Autosomal Dominant Hyper-IgE (Job) Syndrome", correct: true },
        { text: "Atopic Dermatitis", correct: false },
        { text: "Chronic Granulomatous Disease", correct: false },
        { text: "SCID", correct: false }
      ],
      explanation: "Hyper-IgE (Job) Syndrome is characterized by FATED: Facies (coarse), Abscesses (cold/staph), Teeth (retained), IgE (high), Dermatitis. The STAT3 mutation impairs Th17 cell function, preventing proper neutrophil recruitment to skin infections.",
      defect: "STAT3 mutation (Th17 deficiency)",
      infections: "S. aureus, Candida",
      treatment: "Prophylactic antibiotics/antifungals"
    },
    {
      id: 11,
      title: "Case: Retained Baby Teeth",
      age: "8-year-old male",
      presentation: "An 8-year-old boy is seen by a dentist because his primary teeth have not fallen out, resulting in a 'double row' of teeth. He also has a history of multiple bone fractures from minor trauma.",
      physicalExam: "Retained primary teeth. Mild scoliosis is noted. Skin shows lichenification consistent with chronic eczema.",
      labs: "IgE levels are extremely elevated (5,000 IU/mL).",
      buzzwords: ["Retained primary teeth", "Scoliosis", "Bone fractures", "Double row of teeth"],
      options: [
        { text: "Autosomal Dominant Hyper-IgE (Job) Syndrome", correct: true },
        { text: "Chediak-Higashi Syndrome", correct: false },
        { text: "Leukocyte Adhesion Deficiency", correct: false },
        { text: "Wiskott-Aldrich Syndrome", correct: false }
      ],
      explanation: "Job syndrome affects the skeleton and connective tissue as well as the immune system. Retained primary teeth, scoliosis, and pathologic fractures are key non-immune findings that clinch the diagnosis.",
      defect: "STAT3 mutation",
      infections: "Staph abscesses, Candida",
      treatment: "Prophylaxis"
    },
    {
      id: 12,
      title: "Case: Persistent Thrush",
      age: "3-year-old female",
      presentation: "A 3-year-old female is evaluated for oral candidiasis (thrush) that has persisted since birth despite multiple courses of nystatin. She has recently developed hypoparathyroidism.",
      physicalExam: "Thick white plaques on the tongue and buccal mucosa. Fingernails appear thickened, yellow, and dystrophic.",
      labs: "B-cell and T-cell counts are normal. However, T-cells fail to proliferate when exposed to Candida antigen in vitro.",
      buzzwords: ["Candida only", "Thrush", "Nail dystrophy", "Endocrinopathy"],
      options: [
        { text: "Chronic Mucocutaneous Candidiasis", correct: true },
        { text: "Severe Combined Immunodeficiency (SCID)", correct: false },
        { text: "DiGeorge Syndrome", correct: false },
        { text: "CVID", correct: false }
      ],
      explanation: "This is a specific T-cell dysfunction against Candida. Unlike SCID, these patients don't get severe viral/bacterial infections, but they cannot clear yeast. It is often associated with autoimmune endocrine disorders (APECED).",
      defect: "T-cell dysfunction against Candida (AIRE or STAT1 mutations)",
      infections: "Candida albicans (mucocutaneous)",
      treatment: "Systemic antifungals"
    },

    // --- COMBINED B & T CELL DISORDERS ---
    {
      id: 13,
      title: "Case: Failure to Thrive",
      age: "3-month-old male",
      presentation: "A 3-month-old male is admitted for severe failure to thrive and chronic diarrhea. He has had persistent oral thrush for 6 weeks and is now hypoxic with pneumonia.",
      physicalExam: "The infant appears wasted and ill. Chest X-ray reveals an absent thymic shadow. There are no palpable lymph nodes.",
      labs: "Flow cytometry shows very low T cells, very low NK cells, but normal numbers of B cells (T- B+ NK-). However, the B cells are non-functional.",
      buzzwords: ["Bubble boy", "Diarrhea + Thrush + FTT", "IL-2R gamma", "X-linked SCID"],
      options: [
        { text: "SCID (X-linked)", correct: true },
        { text: "DiGeorge Syndrome", correct: false },
        { text: "XLA", correct: false },
        { text: "CVID", correct: false }
      ],
      explanation: "X-linked SCID is the most common form, caused by a mutation in the IL-2 receptor gamma chain (shared by many cytokines). This prevents T and NK cell maturation. B cells are present but don't work because they lack T-cell help.",
      defect: "IL-2R gamma chain mutation",
      infections: "All types (Bacteria, Virus, Fungi, Opportunistic)",
      treatment: "Urgent Stem Cell Transplant"
    },
    {
      id: 14,
      title: "Case: Metabolic SCID",
      age: "2-month-old female",
      presentation: "A 2-month-old female presents with severe respiratory distress. Nasal swab is positive for RSV, Adenovirus, and Parainfluenza simultaneously. She has profound lymphopenia.",
      physicalExam: "Absent lymphoid tissue. 'Rachitic rosary' (rib abnormalities) noted on chest exam.",
      labs: "Flow cytometry shows absence of T cells, B cells, AND NK cells (T- B- NK-). Biochemical testing shows accumulation of dATP in red blood cells.",
      buzzwords: ["ADA deficiency", "Toxic metabolites", "T- B- NK- phenotype", "Rib abnormalities"],
      options: [
        { text: "SCID (ADA Deficiency)", correct: true },
        { text: "XLA", correct: false },
        { text: "Hyper-IgM Syndrome", correct: false },
        { text: "Wiskott-Aldrich Syndrome", correct: false }
      ],
      explanation: "Adenosine Deaminase (ADA) deficiency leads to toxic accumulation of dATP, which is poisonous to developing lymphocytes. This wipes out all cell lines (T, B, and NK). Skeletal abnormalities can also be seen.",
      defect: "Adenosine Deaminase (ADA) deficiency",
      infections: "All types",
      treatment: "Enzyme replacement (PEG-ADA), Gene therapy, Transplant"
    },
    {
      id: 15,
      title: "Case: The Screened Newborn",
      age: "5-day-old male",
      presentation: "A 5-day-old male is called back to the clinic because of an abnormal newborn screen. He is currently asymptomatic, feeding well, and looks healthy.",
      physicalExam: "Normal newborn exam.",
      labs: "PCR analysis from the dried blood spot shows very low T-cell receptor excision circles (TRECs). Confirmatory flow cytometry shows < 300 T cells/uL.",
      buzzwords: ["TRECs", "Newborn screen", "Asymptomatic initially"],
      options: [
        { text: "Severe Combined Immunodeficiency (SCID)", correct: true },
        { text: "DiGeorge Syndrome", correct: false },
        { text: "Leukocyte Adhesion Deficiency", correct: false },
        { text: "Chronic Granulomatous Disease", correct: false }
      ],
      explanation: "TRECs are circular DNA byproducts formed when T-cells rearrange their receptors in the thymus. Low TRECs mean the thymus isn't making new T-cells. This allows us to catch SCID *before* the baby gets sick.",
      defect: "Varies (RAG, IL2RG, ADA)",
      infections: "None yet (protected by isolation)",
      treatment: "Immediate isolation & Transplant workup"
    },
    {
      id: 16,
      title: "Case: The Stumbling Child",
      age: "3-year-old female",
      presentation: "A 3-year-old girl is brought in for 'clumsiness'. Her parents say she learned to walk late and now falls frequently. She has also had three episodes of pneumonia in the last year.",
      physicalExam: "Gait is wide-based and unsteady (ataxic). Inspection of the eyes reveals spider-like blood vessels (telangiectasias) on the conjunctiva.",
      labs: "Serum AFP (alpha-fetoprotein) is elevated. IgA levels are very low.",
      buzzwords: ["Ataxia", "Telangiectasia", "High AFP", "IgA deficiency"],
      options: [
        { text: "Ataxia-Telangiectasia", correct: true },
        { text: "Friedreich's Ataxia", correct: false },
        { text: "Wiskott-Aldrich Syndrome", correct: false },
        { text: "DiGeorge Syndrome", correct: false }
      ],
      explanation: "This is the classic triad of Ataxia-Telangiectasia. The ATM gene defect prevents DNA repair. The elevated AFP is a very specific marker for this condition.",
      defect: "ATM gene mutation (DNA repair defect)",
      infections: "Sinopulmonary (due to low IgA)",
      treatment: "Supportive; aggressive infection management"
    },
    {
      id: 17,
      title: "Case: Sensitivity to Radiation",
      age: "7-year-old male",
      presentation: "A 7-year-old male with a history of recurrent sinus infections is diagnosed with lymphoma. During radiation therapy, he develops severe, blistering skin burns at doses that should be well-tolerated.",
      physicalExam: "Ocular telangiectasias are noted on close exam.",
      labs: "Cytogenetic studies show chromosomal breakages.",
      buzzwords: ["Radiation sensitivity", "DNA repair defect", "Lymphoma risk"],
      options: [
        { text: "Ataxia-Telangiectasia", correct: true },
        { text: "Chediak-Higashi Syndrome", correct: false },
        { text: "Fanconi Anemia", correct: false },
        { text: "SCID", correct: false }
      ],
      explanation: "Patients with A-T are hypersensitive to ionizing radiation because their cells cannot repair the double-strand DNA breaks caused by X-rays/gamma rays. This also predisposes them to cancer.",
      defect: "ATM gene mutation",
      infections: "Respiratory",
      treatment: "Avoid radiation; monitor for malignancy"
    },
    {
      id: 18,
      title: "Case: Opportunistic Pneumonia",
      age: "1-year-old male",
      presentation: "A 1-year-old male presents with severe cough and hypoxia. Silver stain of the lung fluid confirms Pneumocystis jirovecii pneumonia (PJP).",
      physicalExam: "Tonsils are small. No lymphadenopathy.",
      labs: "Serum IgM is elevated (250 mg/dL). However, IgG, IgA, and IgE are nearly undetectable. Flow cytometry shows normal T and B cell counts.",
      buzzwords: ["High IgM", "PJP pneumonia", "Class switch defect"],
      options: [
        { text: "Hyper-IgM Syndrome", correct: true },
        { text: "X-Linked Agammaglobulinemia", correct: false },
        { text: "SCID", correct: false },
        { text: "CVID", correct: false }
      ],
      explanation: "Hyper-IgM Syndrome is caused by a defect in CD40L on T-cells. Without CD40L, B-cells cannot 'class switch' from IgM to IgG/IgA. Crucially, T-cells also use CD40L to activate macrophages; without it, patients get opportunistic infections like PJP.",
      defect: "CD40L (on T cells) or CD40 (on B cells)",
      infections: "PJP, Bacteria, Cryptosporidium",
      treatment: "IVIG, TMP-SMX prophylaxis"
    },
    {
      id: 19,
      title: "Case: Chronic Diarrhea",
      age: "4-year-old male",
      presentation: "A 4-year-old boy has suffered from chronic, watery diarrhea for 6 months, leading to significant weight loss. Stool studies are positive for Cryptosporidium.",
      physicalExam: "Failure to thrive.",
      labs: "Lymph node biopsy reveals an absence of germinal centers. Serum IgM is high.",
      buzzwords: ["Cryptosporidium", "No germinal centers", "High IgM"],
      options: [
        { text: "Hyper-IgM Syndrome", correct: true },
        { text: "SCID", correct: false },
        { text: "Leukocyte Adhesion Deficiency", correct: false },
        { text: "Job Syndrome", correct: false }
      ],
      explanation: "Cryptosporidium is a classic pathogen in Hyper-IgM syndrome (infecting the biliary tree/GI tract). The absence of germinal centers occurs because B-cell activation and class switching happen there, driven by CD40L.",
      defect: "Defect in Class Switch Recombination (CD40L)",
      infections: "Cryptosporidium (GI/Biliary)",
      treatment: "Boil water (avoid cysts), IVIG"
    },
    {
      id: 20,
      title: "Case: Ulcers and Low Neutrophils",
      age: "2-year-old male",
      presentation: "A 2-year-old male presents with painful mouth sores and fever. He has a history of pneumonia.",
      physicalExam: "Severe stomatitis (mouth ulcers) and gingivitis.",
      labs: "CBC shows severe neutropenia (< 500 cells). Immunoglobulins show High IgM and low IgG.",
      buzzwords: ["Neutropenia", "Oral ulcers", "CD40L defect"],
      options: [
        { text: "Hyper-IgM Syndrome", correct: true },
        { text: "Chediak-Higashi Syndrome", correct: false },
        { text: "Leukocyte Adhesion Deficiency", correct: false },
        { text: "Wiskott-Aldrich Syndrome", correct: false }
      ],
      explanation: "Neutropenia is a very common, unexplained feature of X-linked Hyper-IgM syndrome (occurring in ~50% of patients), causing mouth ulcers. This distinguishes it from other antibody defects.",
      defect: "CD40L mutation",
      infections: "Bacteria",
      treatment: "G-CSF for neutropenia, IVIG"
    },
    {
      id: 21,
      title: "Case: The Eczema-Bleeding Triad",
      age: "1-year-old male",
      presentation: "A 1-year-old male is referred for 'severe eczema' that has become infected. His parents also note he gets bloody noses easily and had blood in his stool last week.",
      physicalExam: "Diffuse eczema with excoriations. Petechiae are visible on the extremities. Otoscopy shows bilateral ear infections.",
      labs: "CBC shows Thrombocytopenia (Platelets 35,000). The platelets are noted to be unusually small.",
      buzzwords: ["WATER: Wiskott-Aldrich, Thrombocytopenia, Eczema, Recurrent infections", "Small platelets"],
      options: [
        { text: "Wiskott-Aldrich Syndrome", correct: true },
        { text: "Job Syndrome", correct: false },
        { text: "Ataxia-Telangiectasia", correct: false },
        { text: "ITP", correct: false }
      ],
      explanation: "This is the classic WAS triad. The key lab finding is 'small platelets' (low MPV), which helps rule out ITP (where platelets are large/normal).",
      defect: "WAS gene mutation (cytoskeleton defect)",
      infections: "Encapsulated bacteria, opportunistic later",
      treatment: "Stem Cell Transplant"
    },
    {
      id: 22,
      title: "Case: Prolonged Bleeding",
      age: "3-day-old male",
      presentation: "A 3-day-old male has persistent oozing of blood from his circumcision site, requiring pressure and topical agents to stop.",
      physicalExam: "Scattered petechiae on the trunk.",
      labs: "Platelet count is 30,000. Mean Platelet Volume (MPV) is low.",
      buzzwords: ["Circumcision bleeding", "Low MPV (Small platelets)", "X-linked"],
      options: [
        { text: "Wiskott-Aldrich Syndrome", correct: true },
        { text: "Hemophilia A", correct: false },
        { text: "Leukocyte Adhesion Deficiency", correct: false },
        { text: "Chronic Granulomatous Disease", correct: false }
      ],
      explanation: "Wiskott-Aldrich often presents in the neonatal period simply as bleeding due to thrombocytopenia, before the eczema or infections start. The small platelet size is diagnostic.",
      defect: "Actin cytoskeleton defect",
      infections: "Late onset",
      treatment: "Platelet support, Transplant"
    },
    {
      id: 23,
      title: "Case: The Ig Pattern",
      age: "5-year-old male",
      presentation: "A 5-year-old boy with eczema and recurrent pneumonia is evaluated by immunology.",
      labs: "Quantitative immunoglobulins show Elevated IgE and Elevated IgA. IgM is low. IgG is normal.",
      buzzwords: ["High IgE/IgA", "Low IgM", "Eczema"],
      options: [
        { text: "Wiskott-Aldrich Syndrome", correct: true },
        { text: "Job Syndrome", correct: false },
        { text: "Hyper-IgM Syndrome", correct: false },
        { text: "Selective IgA Deficiency", correct: false }
      ],
      explanation: "The classic immunoglobulin pattern in WAS is High IgA/IgE and Low IgM. This explains the allergic/eczema component (IgE) and susceptibility to encapsulated bacteria (low IgM/antibody response).",
      defect: "WAS gene",
      infections: "Encapsulated bacteria",
      treatment: "IVIG, Transplant"
    },

    // --- PHAGOCYTIC DISORDERS ---
    {
      id: 24,
      title: "Case: Fungal Pneumonia",
      age: "6-year-old male",
      presentation: "A 6-year-old male presents with fever and cough. He has a history of multiple skin abscesses. A lung biopsy reveals fungal hyphae identified as Aspergillus fumigatus.",
      physicalExam: "Hepatomegaly is noted.",
      labs: "Dihydrorhodamine (DHR) flow cytometry shows no fluorescence shift.",
      buzzwords: ["Aspergillus", "Abnormal DHR", "Catalase positive organisms"],
      options: [
        { text: "Chronic Granulomatous Disease (CGD)", correct: true },
        { text: "CVID", correct: false },
        { text: "Leukocyte Adhesion Deficiency", correct: false },
        { text: "MPO Deficiency", correct: false }
      ],
      explanation: "CGD patients lack the 'respiratory burst' (NADPH oxidase). They cannot produce bleach/superoxide to kill Catalase Positive organisms like Aspergillus, Staph, and Serratia.",
      defect: "NADPH Oxidase deficiency",
      infections: "Catalase (+) Bacteria and Fungi",
      treatment: "Antifungal prophylaxis, IFN-gamma"
    },
    {
      id: 25,
      title: "Case: Osteomyelitis",
      age: "10-year-old male",
      presentation: "A 10-year-old male presents with bone pain in his ribs. He has no history of trauma. Culture of the bone lesion grows Serratia marcescens.",
      physicalExam: "Draining lymph nodes in the axilla.",
      labs: "Nitroblue Tetrazolium (NBT) test fails to turn blue.",
      buzzwords: ["Serratia", "Osteomyelitis", "Negative NBT"],
      options: [
        { text: "Chronic Granulomatous Disease (CGD)", correct: true },
        { text: "Leukocyte Adhesion Deficiency", correct: false },
        { text: "Job Syndrome", correct: false },
        { text: "X-Linked Agammaglobulinemia", correct: false }
      ],
      explanation: "Serratia is a classic catalase-positive bug associated with CGD osteomyelitis. The negative NBT test confirms the neutrophils failed to produce superoxide.",
      defect: "NADPH Oxidase",
      infections: "Serratia marcescens",
      treatment: "Antibiotics, Surgery"
    },
    {
      id: 26,
      title: "Case: Obstruction",
      age: "18-year-old male",
      presentation: "An 18-year-old male presents with persistent vomiting and weight loss. He has a history of recurrent skin infections.",
      physicalExam: "Abdominal imaging shows narrowing of the gastric outlet due to a mass-like lesion.",
      labs: "Biopsy of the mass shows a granuloma with no organisms seen.",
      buzzwords: ["Granuloma", "GI obstruction", "X-linked"],
      options: [
        { text: "Chronic Granulomatous Disease (CGD)", correct: true },
        { text: "CVID", correct: false },
        { text: "Crohn's Disease", correct: false },
        { text: "Leukocyte Adhesion Deficiency", correct: false }
      ],
      explanation: "Because CGD neutrophils cannot kill bacteria efficiently, the immune system walls them off in granulomas. These granulomas can become large enough to obstruct the stomach (pyloric stenosis) or ureters.",
      defect: "NADPH Oxidase",
      infections: "Catalase (+)",
      treatment: "Steroids for granulomas"
    },
    {
      id: 27,
      title: "Case: The Cord That Won't Fall",
      age: "4-week-old female",
      presentation: "A 4-week-old infant is brought in because her umbilical cord stump is still attached. The area around it is red and angry.",
      physicalExam: "Omphalitis (infection of the umbilicus).",
      labs: "WBC count is extremely elevated (50,000/uL), mostly neutrophils.",
      buzzwords: ["Delayed cord separation (>30 days)", "High WBC", "Neutrophilia"],
      options: [
        { text: "Leukocyte Adhesion Deficiency (LAD)", correct: true },
        { text: "Chronic Granulomatous Disease", correct: false },
        { text: "SCID", correct: false },
        { text: "Chediak-Higashi Syndrome", correct: false }
      ],
      explanation: "LAD Type 1 is a defect in CD18 (integrins). Neutrophils are made (high WBC) but cannot stick to the vessel wall to exit into tissues. They can't get to the cord to degrade it, so it stays attached.",
      defect: "CD18 (Integrin) deficiency",
      infections: "Bacterial (no pus)",
      treatment: "Stem Cell Transplant"
    },
    {
      id: 28,
      title: "Case: Infection Without Pus",
      age: "2-year-old female",
      presentation: "A 2-year-old female presents with severe gum infection (periodontitis) and a skin ulcer on her leg that is not healing.",
      physicalExam: "The skin ulcer is large and necrotic but notably lacks pus.",
      labs: "Flow cytometry shows absence of CD18 on the surface of neutrophils.",
      buzzwords: ["No pus", "Periodontitis", "Absent CD18"],
      options: [
        { text: "Leukocyte Adhesion Deficiency (LAD)", correct: true },
        { text: "Chronic Granulomatous Disease", correct: false },
        { text: "Job Syndrome", correct: false },
        { text: "Neutropenia", correct: false }
      ],
      explanation: "Pus is composed of dead neutrophils. Since neutrophils cannot exit the blood vessels in LAD, the infections are 'cold' and lack pus formation.",
      defect: "Leukocyte adhesion failure",
      infections: "Skin/Mucosal",
      treatment: "Antibiotics, Transplant"
    },
    {
      id: 29,
      title: "Case: Silver Hair",
      age: "3-year-old male",
      presentation: "A 3-year-old male is evaluated for recurrent skin abscesses. His parents note that he bruises easily.",
      physicalExam: "The child has very fair skin, light blue eyes, and hair that has a silvery-metallic sheen.",
      labs: "Peripheral blood smear reveals neutrophils with massive, giant granules.",
      buzzwords: ["Partial albinism", "Silver hair", "Giant granules"],
      options: [
        { text: "Chediak-Higashi Syndrome", correct: true },
        { text: "Griscelli Syndrome", correct: false },
        { text: "Hermansky-Pudlak", correct: false },
        { text: "Albinism", correct: false }
      ],
      explanation: "The LYST gene mutation affects lysosomal trafficking. Melanosomes get stuck (causing albinism) and lysosomes fuse together (causing giant granules).",
      defect: "LYST gene (Microtubule/Lysosomal dysfunction)",
      infections: "Pyogenic bacteria",
      treatment: "Transplant"
    },
    {
      id: 30,
      title: "Case: Progressive Weakness",
      age: "6-year-old female",
      presentation: "A 6-year-old female with a history of albinism presents with increasing clumsiness and weakness in her legs.",
      physicalExam: "Nystagmus is present. Deep tendon reflexes are absent.",
      labs: "CBC shows mild neutropenia.",
      buzzwords: ["Neuropathy", "Nystagmus", "Albinism", "Chediak-Higashi"],
      options: [
        { text: "Chediak-Higashi Syndrome", correct: true },
        { text: "Ataxia-Telangiectasia", correct: false },
        { text: "DiGeorge Syndrome", correct: false },
        { text: "Chronic inflammatory demyelinating polyneuropathy", correct: false }
      ],
      explanation: "In addition to immune defects, Chediak-Higashi causes progressive neurologic dysfunction (neuropathy) due to the accumulation of abnormal lysosomes in nerve cells.",
      defect: "Lysosomal trafficking",
      infections: "Pyogenic",
      treatment: "Supportive"
    },
    {
      id: 31,
      title: "Case: The Accelerated Phase",
      age: "5-year-old male",
      presentation: "A 5-year-old male with known Chediak-Higashi syndrome presents with high fever, jaundice, and abdominal distension.",
      physicalExam: "Massive hepatosplenomegaly.",
      labs: "Pancytopenia (low RBC, WBC, platelets). Ferritin is extremely high.",
      buzzwords: ["HLH (Hemophagocytic Lymphohistiocytosis)", "Pancytopenia", "Giant granules"],
      options: [
        { text: "Chediak-Higashi Syndrome (Accelerated Phase)", correct: true },
        { text: "Leukemia", correct: false },
        { text: "Chronic Granulomatous Disease", correct: false },
        { text: "SCID", correct: false }
      ],
      explanation: "Most patients with Chediak-Higashi eventually enter the 'accelerated phase', which is a lymphoproliferative syndrome (HLH) usually triggered by a viral infection (EBV). It is fatal without treatment.",
      defect: "LYST gene",
      infections: "Viral trigger",
      treatment: "Chemotherapy / Transplant"
    },

    // --- COMPLEMENT DISORDERS ---
    {
      id: 32,
      title: "Case: College Outbreak",
      age: "19-year-old female",
      presentation: "A 19-year-old college student presents with headache, stiff neck, and photophobia. She had a similar episode of meningitis two years ago.",
      physicalExam: "Positive Brudzinski and Kernig signs.",
      labs: "CSF grows Neisseria meningitidis. CH50 assay is zero.",
      buzzwords: ["Recurrent meningitis", "Neisseria", "Low CH50"],
      options: [
        { text: "Terminal Complement (C5-C9) Deficiency", correct: true },
        { text: "CVID", correct: false },
        { text: "Splenectomy", correct: false },
        { text: "Properdin Deficiency", correct: false }
      ],
      explanation: "The Membrane Attack Complex (MAC), formed by C5-C9, is the primary mechanism for killing Neisseria species. Deficiency leaves patients uniquely susceptible to recurrent meningitis.",
      defect: "C5-C9 deficiency",
      infections: "Neisseria meningitidis",
      treatment: "Meningococcal Vaccination"
    },
    {
      id: 33,
      title: "Case: Disseminated Gonorrhea",
      age: "25-year-old male",
      presentation: "A 25-year-old male presents with a swollen, painful knee and pustular skin rash. He reports a history of urethritis.",
      physicalExam: "Joint aspirate grows Neisseria gonorrhoeae.",
      labs: "Testing shows an inability to form the Membrane Attack Complex.",
      buzzwords: ["Disseminated Gonorrhea", "Septic arthritis", "MAC complex"],
      options: [
        { text: "Terminal Complement (C5-C9) Deficiency", correct: true },
        { text: "HIV", correct: false },
        { text: "IgA Deficiency", correct: false },
        { text: "X-Linked Agammaglobulinemia", correct: false }
      ],
      explanation: "Susceptibility isn't limited to meningitis; these patients are also prone to systemic dissemination of Gonorrhea (septic arthritis).",
      defect: "MAC formation failure",
      infections: "Neisseria gonorrhoeae",
      treatment: "Antibiotics"
    },
    
    // --- VARIATIONS ---
    {
      id: 34,
      title: "Case: Chronic Meningitis",
      age: "5-year-old male",
      presentation: "A 5-year-old boy with a history of recurrent pneumonia presents with progressive confusion and headaches. MRI shows chronic inflammation.",
      labs: "CSF PCR is positive for Enterovirus. Serum immunoglobulins are undetectable.",
      buzzwords: ["Enterovirus", "Meningoencephalitis", "XLA"],
      options: [
        { text: "Bruton's X-linked Agammaglobulinemia", correct: true },
        { text: "CVID", correct: false },
        { text: "T-cell deficiency", correct: false },
        { text: "Complement deficiency", correct: false }
      ],
      explanation: "While XLA is known for bacterial infections, antibodies are crucial for neutralizing Enteroviruses (Echo/Coxsackie) in the gut. Without them, the virus disseminates to the brain.",
      defect: "BTK mutation",
      infections: "Enterovirus (Meningoencephalitis)",
      treatment: "High dose IVIG"
    },
    {
      id: 35,
      title: "Case: The Red Baby",
      age: "2-month-old male",
      presentation: "A 2-month-old male presents with bright red skin peeling over his entire body (erythroderma). He also has chronic diarrhea.",
      labs: "CBC shows Eosinophilia. IgE is High. Flow cytometry shows low B cells but the presence of oligoclonal T cells.",
      buzzwords: ["Omenn Syndrome", "Erythroderma", "RAG mutation", "Eosinophilia"],
      options: [
        { text: "SCID (Omenn Syndrome variant)", correct: true },
        { text: "Job Syndrome", correct: false },
        { text: "Atopic Dermatitis", correct: false },
        { text: "IPEX Syndrome", correct: false }
      ],
      explanation: "Omenn Syndrome is a 'leaky' SCID (often RAG mutations) where a few T cells develop but are abnormal and autoreactive, causing severe skin redness and eosinophilia.",
      defect: "Partial RAG activity",
      infections: "Opportunistic",
      treatment: "Transplant"
    },
    {
      id: 36,
      title: "Case: Lung Abscess",
      age: "25-year-old male",
      presentation: "A 25-year-old male presents with a brain abscess and lung consolidation. Culture grows Nocardia.",
      physicalExam: "He has extensive scarring on his face and neck from childhood boils.",
      labs: "DHR test is abnormal (reduced fluorescence).",
      buzzwords: ["Nocardia", "Catalase positive", "Adult diagnosis"],
      options: [
        { text: "Chronic Granulomatous Disease (CGD)", correct: true },
        { text: "CVID", correct: false },
        { text: "HIV", correct: false },
        { text: "Job Syndrome", correct: false }
      ],
      explanation: "CGD can sometimes be diagnosed in adulthood if the mutation leaves some residual NADPH oxidase function. Nocardia is a classic catalase-positive pathogen.",
      defect: "NADPH oxidase",
      infections: "Nocardia",
      treatment: "Antibiotics"
    },
    {
      id: 37,
      title: "Case: Speech Delay",
      age: "4-year-old female",
      presentation: "A 4-year-old female is seen for speech delay and hypernasal voice. She had a heart defect repaired in infancy.",
      physicalExam: "Submucous cleft palate is identified.",
      labs: "T-cell count is only mildly decreased. Calcium is normal.",
      buzzwords: ["Hypernasal speech", "Mild T-cell defect", "Submucous cleft"],
      options: [
        { text: "Partial DiGeorge Syndrome", correct: true },
        { text: "SCID", correct: false },
        { text: "IgA Deficiency", correct: false },
        { text: "CVID", correct: false }
      ],
      explanation: "DiGeorge is a spectrum. Many patients have 'Partial' DiGeorge with only mild T-cell lymphopenia and survive into adulthood without transplant. The speech issue is due to the palate defect.",
      defect: "22q11.2",
      infections: "Mild viral",
      treatment: "Speech therapy, Supportive"
    },
    {
      id: 38,
      title: "Case: New Mass",
      age: "12-year-old male",
      presentation: "A 12-year-old male with a known history of thrombocytopenia and eczema presents with a rapidly enlarging neck mass.",
      physicalExam: "Firm, non-tender lymph node measuring 3cm.",
      labs: "Biopsy confirms EBV-positive B-cell Lymphoma.",
      buzzwords: ["Malignancy risk", "Eczema/Thrombocytopenia history"],
      options: [
        { text: "Wiskott-Aldrich Syndrome", correct: true },
        { text: "X-Linked Agammaglobulinemia", correct: false },
        { text: "Chronic Granulomatous Disease", correct: false },
        { text: "Leukocyte Adhesion Deficiency", correct: false }
      ],
      explanation: "WAS patients have a significantly increased risk of malignancies, particularly EBV-related lymphomas, due to poor T-cell surveillance of the virus.",
      defect: "WAS gene",
      infections: "EBV driven malignancy",
      treatment: "Chemo/Transplant"
    },
    {
      id: 39,
      title: "Case: Lung Cysts",
      age: "15-year-old female",
      presentation: "A 15-year-old female has a routine chest X-ray for sports clearance. It shows large air-filled cysts (pneumatoceles).",
      physicalExam: "She has mild scoliosis and rough skin consistent with eczema.",
      labs: "IgE is >1000 IU/mL.",
      buzzwords: ["Pneumatocele", "Post-staph pneumonia", "High IgE"],
      options: [
        { text: "Autosomal Dominant Hyper-IgE (Job) Syndrome", correct: true },
        { text: "Cystic Fibrosis", correct: false },
        { text: "Alpha-1 Antitrypsin", correct: false },
        { text: "Tuberculosis", correct: false }
      ],
      explanation: "Recurrent Staph pneumonias in Job syndrome typically heal with pneumatocele (cyst) formation, which can persist for years.",
      defect: "STAT3 mutation",
      infections: "Staph aureus",
      treatment: "Prophylaxis"
    },
    {
      id: 40,
      title: "Case: Rash and Joint Pain",
      age: "28-year-old female",
      presentation: "A 28-year-old female presents with a butterfly rash on her face that gets worse in the sun, and joint pain in her hands.",
      labs: "ANA is positive. C1q, C2, and C4 levels are undetectable.",
      buzzwords: ["Early complement deficiency (C1-C4)", "SLE-like illness"],
      options: [
        { text: "Early Complement Deficiency (C1-C4)", correct: true },
        { text: "Terminal Complement Deficiency", correct: false },
        { text: "CVID", correct: false },
        { text: "Chronic Granulomatous Disease", correct: false }
      ],
      explanation: "While C5-C9 deficiency causes Neisseria infections, EARLY complement deficiencies (C1, C2, C4) are strongly associated with autoimmune diseases like Lupus (SLE) because early complement is needed to clear immune complexes from the blood.",
      defect: "Failure to clear immune complexes",
      infections: "Encapsulated bacteria (mild)",
      treatment: "Manage SLE"
    }
  ];

  // --- REVERSE REASONING: CASE-STYLE SCENARIOS ---
  const reverseQuestionsData = [
    {
      id: 1,
      diagnosis: "X-Linked Agammaglobulinemia (XLA)",
      question: "A 5-year-old male with known XLA presents with chronic headaches and cognitive decline. MRI shows chronic meningoencephalitis. Which pathogen is the most likely cause?",
      options: [
        { text: "Enterovirus (Echovirus)", correct: true },
        { text: "Streptococcus pneumoniae", correct: false },
        { text: "Aspergillus fumigatus", correct: false },
        { text: "Candida albicans", correct: false }
      ],
      explanation: "While XLA patients are prone to bacteria, antibodies are critical for neutralizing Enteroviruses in the gut. Without them, the virus can disseminate to the CNS."
    },
    {
      id: 2,
      diagnosis: "Chronic Granulomatous Disease",
      question: "A patient with CGD presents with a liver abscess. You need to start empiric antibiotics. Which organism must you cover for?",
      options: [
        { text: "Staphylococcus aureus (Catalase Positive)", correct: true },
        { text: "Streptococcus pyogenes (Catalase Negative)", correct: false },
        { text: "Haemophilus influenzae", correct: false },
        { text: "Pneumocystis jirovecii", correct: false }
      ],
      explanation: "CGD neutrophils cannot produce the oxidative burst to kill Catalase Positive organisms like Staph, Serratia, and Aspergillus."
    },
    {
      id: 3,
      diagnosis: "Leukocyte Adhesion Deficiency",
      question: "An infant with LAD presents with a severe perirectal abscess. The surgeon incises the area. What do you expect to see?",
      options: [
        { text: "Necrotic tissue with no pus", correct: true },
        { text: "Copious purulent discharge", correct: false },
        { text: "Granuloma formation", correct: false },
        { text: "Caseating necrosis", correct: false }
      ],
      explanation: "Pus is made of dead neutrophils. In LAD, neutrophils cannot exit the blood vessels to reach the infection site, so infections are 'cold' and lack pus."
    },
    {
      id: 4,
      diagnosis: "SCID",
      question: "A newborn is diagnosed with SCID via screening. The parents ask about vaccination schedule. Which of the following is strictly CONTRAINDICATED?",
      options: [
        { text: "Rotavirus Vaccine (Live Attenuated)", correct: true },
        { text: "Hepatitis B (Recombinant)", correct: false },
        { text: "DTaP (Toxoid)", correct: false },
        { text: "IPV (Inactivated Polio)", correct: false }
      ],
      explanation: "Live vaccines (Rotavirus, MMR, Varicella, BCG) can cause fatal disseminated infection in SCID patients and must be avoided."
    },
    {
      id: 5,
      diagnosis: "Wiskott-Aldrich Syndrome",
      question: "A 10-year-old with WAS presents with fatigue and a new neck mass. Biopsy confirms lymphoma. Which virus is the likely driver of this malignancy?",
      options: [
        { text: "Epstein-Barr Virus (EBV)", correct: true },
        { text: "Cytomegalovirus (CMV)", correct: false },
        { text: "Herpes Simplex Virus (HSV)", correct: false },
        { text: "Adenovirus", correct: false }
      ],
      explanation: "WAS patients have defective T-cell surveillance against EBV, leading to a high risk of EBV-driven B-cell lymphomas."
    },
    {
      id: 6,
      diagnosis: "Selective IgA Deficiency",
      question: "A patient with Selective IgA deficiency is in a car accident and needs blood. What specific request must you make to the blood bank?",
      options: [
        { text: "Washed RBCs or IgA-deficient products", correct: true },
        { text: "Irradiated RBCs", correct: false },
        { text: "CMV-negative blood", correct: false },
        { text: "Whole blood only", correct: false }
      ],
      explanation: "These patients may have anti-IgA antibodies. Receiving standard blood (which contains IgA) can trigger fatal anaphylaxis."
    },
    {
      id: 7,
      diagnosis: "Hyper-IgM Syndrome",
      question: "A child with Hyper-IgM syndrome presents with cough and hypoxia. You suspect an opportunistic infection usually seen in T-cell defects. What is it?",
      options: [
        { text: "Pneumocystis jirovecii", correct: true },
        { text: "Staphylococcus aureus", correct: false },
        { text: "Pseudomonas aeruginosa", correct: false },
        { text: "Neisseria meningitidis", correct: false }
      ],
      explanation: "The CD40L defect prevents T-cells from activating macrophages. This leaves patients vulnerable to PJP, similar to HIV/SCID patients."
    },
    {
      id: 8,
      diagnosis: "Terminal Complement Deficiency",
      question: "A college student is diagnosed with C6 deficiency. What prophylactic measure is most important?",
      options: [
        { text: "Meningococcal Vaccination", correct: true },
        { text: "Monthly IVIG", correct: false },
        { text: "Life-long antifungal prophylaxis", correct: false },
        { text: "Avoidance of live vaccines", correct: false }
      ],
      explanation: "The MAC complex is essential for killing Neisseria meningitidis. Vaccination is critical to boost antibody-mediated killing (opsonization)."
    },
    {
      id: 9,
      diagnosis: "Hyper-IgE (Job) Syndrome",
      question: "You are examining a patient with Job syndrome. Aside from skin abscesses, what skeletal finding would you expect to see?",
      options: [
        { text: "Retained primary teeth (Double row)", correct: true },
        { text: "Absent radius bone", correct: false },
        { text: "Rachitic rosary", correct: false },
        { text: "Blue sclera", correct: false }
      ],
      explanation: "Failure of primary teeth to fall out is a hallmark non-immune feature of Job syndrome."
    },
    {
      id: 10,
      diagnosis: "DiGeorge Syndrome",
      question: "A newborn has a positive screen for 22q11.2 deletion. The nurse reports the baby is jittery and having tremors. What lab do you check immediately?",
      options: [
        { text: "Serum Calcium", correct: true },
        { text: "Blood Glucose", correct: false },
        { text: "White Blood Cell Count", correct: false },
        { text: "Platelet Count", correct: false }
      ],
      explanation: "Parathyroid hypoplasia causes hypocalcemia, which presents as tetany, seizures, or jitteriness in the newborn period."
    }
  ];

  const diseaseDetails = [
    {
      category: "B-Cell Disorders",
      diseases: [
        {
          name: "Bruton's X-Linked Agammaglobulinemia (XLA)",
          gene: "BTK mutation",
          defect: "B-cell maturation arrest at pre-B cell stage",
          key: "Absent tonsils, No B cells, recurrent bacterial infections >6mo.",
          inheritance: "X-Linked Recessive",
          onset: ">6 months",
          labs: "Low IgG/A/M, Absent B cells",
          treatment: "Lifelong IVIG"
        },
        {
          name: "Selective IgA Deficiency",
          gene: "Unknown",
          defect: "Class switch failure to IgA",
          key: "Asymptomatic, Giardia risk, Anaphylaxis to blood.",
          inheritance: "Variable",
          onset: "Any age",
          labs: "Low IgA, Normal IgG/M",
          treatment: "Observation (Treat infections)"
        },
        {
          name: "Common Variable Immunodeficiency (CVID)",
          gene: "Various",
          defect: "B-cell differentiation defect to Plasma Cells",
          key: "Young adults, Autoimmunity, Lymphoma, Normal B cell count.",
          inheritance: "Sporadic",
          onset: "20s-30s",
          labs: "Low IgG/A, Low Memory B cells",
          treatment: "IVIG"
        }
      ]
    },
    {
      category: "T-Cell Disorders",
      diseases: [
        {
          name: "Thymic Aplasia (DiGeorge)",
          gene: "22q11.2 deletion",
          defect: "Thymic/Parathyroid hypoplasia (3rd/4th pouch)",
          key: "CATCH-22, Hypocalcemia, Cardiac defects, Absent thymus.",
          inheritance: "Autosomal Dominant",
          onset: "Birth",
          labs: "Low T cells, Low Calcium, Absent thymic shadow",
          treatment: "Thymus transplant, Calcium"
        },
        {
          name: "IL-12 Receptor Deficiency",
          gene: "IL-12R",
          defect: "Th1/Macrophage interaction failure",
          key: "Mycobacterial infections (BCG), Salmonella.",
          inheritance: "Autosomal Recessive",
          onset: "Childhood",
          labs: "Low IFN-gamma response",
          treatment: "Antibiotics"
        },
        {
          name: "Hyper-IgE (Job) Syndrome",
          gene: "STAT3",
          defect: "Th17 deficiency (impaired neutrophil recruitment)",
          key: "FATED: Facies, Cold Abscesses, Teeth retained, IgE high, Dermatitis.",
          inheritance: "Autosomal Dominant",
          onset: "Infancy",
          labs: "IgE > 2000, Eosinophilia",
          treatment: "Prophylaxis"
        },
        {
          name: "Chronic Mucocutaneous Candidiasis",
          gene: "AIRE/STAT1",
          defect: "T-cell response to Candida",
          key: "Persistent thrush, nail dystrophy, endocrine issues.",
          inheritance: "Variable",
          onset: "Infancy",
          labs: "Anergy to Candida",
          treatment: "Antifungals"
        }
      ]
    },
    {
      category: "Combined B & T Disorders",
      diseases: [
        {
          name: "SCID",
          gene: "IL2RG (X-linked), ADA (AR)",
          defect: "Absent T and B function",
          key: "Emergency! FTT, Thrush, Diarrhea. Avoid live vaccines.",
          inheritance: "X-linked / AR",
          onset: "<3 months",
          labs: "Low T cells, Low TRECs, Absent thymic shadow",
          treatment: "Stem Cell Transplant"
        },
        {
          name: "Ataxia-Telangiectasia",
          gene: "ATM (DNA repair)",
          defect: "B/T cell + CNS + Skin (DNA repair defect)",
          key: "Ataxia, Spider angiomas, IgA deficiency, Radiation sensitivity.",
          inheritance: "Autosomal Recessive",
          onset: "Toddler (walking)",
          labs: "High AFP, Low IgA",
          treatment: "Supportive"
        },
        {
          name: "Hyper-IgM Syndrome",
          gene: "CD40L (X-linked)",
          defect: "Class switch failure",
          key: "High IgM, Low others. PJP pneumonia, Neutropenia.",
          inheritance: "X-Linked",
          onset: "Infancy",
          labs: "High IgM, Low IgG/A, Normal B/T counts",
          treatment: "IVIG"
        },
        {
          name: "Wiskott-Aldrich Syndrome",
          gene: "WAS (cytoskeleton)",
          defect: "Platelet/Lymphocyte actin cytoskeleton defect",
          key: "WATER triad: Eczema, Thrombocytopenia (Small platelets), Infections.",
          inheritance: "X-Linked",
          onset: "Infancy",
          labs: "Small platelets (Low MPV), High IgE/A",
          treatment: "Transplant"
        }
      ]
    },
    {
      category: "Phagocytic Disorders",
      diseases: [
        {
          name: "Chronic Granulomatous Disease (CGD)",
          gene: "NADPH Oxidase",
          defect: "No oxidative burst (ROS production)",
          key: "Catalase+ bugs (Staph, Aspergillus). Negative NBT/Abnormal DHR.",
          inheritance: "X-Linked",
          onset: "Childhood",
          labs: "Abnormal DHR (no shift), Negative NBT",
          treatment: "Antibiotics"
        },
        {
          name: "Leukocyte Adhesion Deficiency (LAD)",
          gene: "CD18 (Integrin)",
          defect: "Neutrophil migration failure",
          key: "Delayed cord separation, High WBC, No pus.",
          inheritance: "Autosomal Recessive",
          onset: "Neonatal",
          labs: "WBC > 50k, Absent CD18",
          treatment: "Transplant"
        },
        {
          name: "Chediak-Higashi Syndrome",
          gene: "LYST",
          defect: "Lysosomal trafficking (microtubule dysfunction)",
          key: "Albinism, Giant granules, Neuropathy.",
          inheritance: "Autosomal Recessive",
          onset: "Infancy",
          labs: "Giant granules on smear",
          treatment: "Transplant"
        }
      ]
    },
    {
      category: "Complement Disorders",
      diseases: [
        {
          name: "Terminal Complement (C5-C9)",
          gene: "C5-C9",
          defect: "MAC formation failure",
          key: "Recurrent Neisseria meningitis/gonorrhea.",
          inheritance: "Variable",
          onset: "Variable",
          labs: "Low CH50",
          treatment: "Vaccine"
        }
      ]
    }
  ];

  // Helper to shuffle any array
  const shuffleArray = (array) => {
    const newArray = [...array];
    for (let i = newArray.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [newArray[i], newArray[j]] = [newArray[j], newArray[i]];
    }
    return newArray;
  };

  const handleModeChange = (newMode) => {
    if (newMode === 'play') {
      // 1. Shuffle the order of cases
      const shuffledCases = shuffleArray([...casesData]);
      // 2. Shuffle the options within each case so A isnt always correct
      const fullyShuffled = shuffledCases.map(c => ({
        ...c,
        options: shuffleArray(c.options)
      }));
      setActiveQuestions(fullyShuffled);
    } else if (newMode === 'reverse') {
      const shuffledReverse = shuffleArray([...reverseQuestionsData]);
      const fullyShuffled = shuffledReverse.map(q => ({
        ...q,
        options: shuffleArray(q.options)
      }));
      setActiveQuestions(fullyShuffled);
    }
    setScore(0);
    setCurrentCase(0);
    setSelectedAnswer(null);
    setShowExplanation(false);
    setGameComplete(false);
    setMode(newMode);
  };

  const handleAnswer = (optionIndex) => {
    if (showExplanation) return;
    
    setSelectedAnswer(optionIndex);
    setShowExplanation(true);
    
    const currentQuestion = activeQuestions[currentCase];
    if (currentQuestion.options[optionIndex].correct) {
      setScore(score + 1);
    }
  };

  const nextCase = () => {
    if (currentCase < activeQuestions.length - 1) {
      setCurrentCase(currentCase + 1);
      setSelectedAnswer(null);
      setShowExplanation(false);
    } else {
      setGameComplete(true);
    }
  };

  const renderFooter = () => (
    <div className="text-center text-gray-500 text-xs mt-8 pb-4">
      <p className="font-semibold">ImmunoDx Creators:</p>
      <p>Kishu Chaudry, Taral Jella, Tara Menon, Austin Fitzgerald, Emily Duggan, Maryam Iqbal, Nikki Akula</p>
    </div>
  );

  const renderMenu = () => (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-8">
      <div className="max-w-4xl mx-auto">
        <div className="bg-white rounded-2xl shadow-2xl p-8 mb-6">
          <div className="text-center mb-8">
            <div className="flex items-center justify-center mb-4">
              <Sparkles className="w-12 h-12 text-indigo-600 mr-3" />
              <h1 className="text-4xl font-bold text-gray-800">ImmunoDx</h1>
            </div>
            <p className="text-xl text-gray-600">From Cells to Syndromes: Primary Immunodeficiencies Learning Tool</p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 mb-8">
            <button
              onClick={() => setMode('learn')}
              className="bg-gradient-to-r from-green-500 to-emerald-600 text-white p-6 rounded-xl hover:from-green-600 hover:to-emerald-700 transition-all transform hover:scale-105 shadow-lg"
            >
              <BookOpen className="w-10 h-10 mx-auto mb-3" />
              <h2 className="text-xl font-bold mb-2">Learn Mode</h2>
              <p className="text-green-50 text-sm">Review Objectives & Timeline</p>
            </button>

            <button
              onClick={() => handleModeChange('play')}
              className="bg-gradient-to-r from-indigo-500 to-purple-600 text-white p-6 rounded-xl hover:from-indigo-600 hover:to-purple-700 transition-all transform hover:scale-105 shadow-lg"
            >
              <Microscope className="w-10 h-10 mx-auto mb-3" />
              <h2 className="text-xl font-bold mb-2">Clinical Cases</h2>
              <p className="text-indigo-50 text-sm">40 Randomized Vignettes</p>
            </button>

            <button
              onClick={() => handleModeChange('reverse')}
              className="bg-gradient-to-r from-pink-500 to-rose-600 text-white p-6 rounded-xl hover:from-pink-600 hover:to-rose-700 transition-all transform hover:scale-105 shadow-lg"
            >
              <Brain className="w-10 h-10 mx-auto mb-3" />
              <h2 className="text-xl font-bold mb-2">Reverse Reasoning</h2>
              <p className="text-pink-50 text-sm">Management Scenarios</p>
            </button>
          </div>
        </div>
        {renderFooter()}
      </div>
    </div>
  );

  const renderLearnMode = () => (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-100 p-4 md:p-8">
      <div className="max-w-6xl mx-auto">
        <button
          onClick={() => setMode('menu')}
          className="mb-6 px-6 py-2 bg-white text-gray-700 rounded-lg hover:bg-gray-50 shadow"
        >
          ← Back to Menu
        </button>

        <div className="bg-white rounded-2xl shadow-2xl p-6 md:p-8">
          <h1 className="text-3xl font-bold text-center mb-8 text-gray-800">Learning Objectives & Review</h1>

          <div className="flex flex-wrap gap-2 mb-8 justify-center">
            {[
              { id: 'overview', label: 'Overview' },
              { id: 'B-Cell', label: 'B-Cell' },
              { id: 'T-Cell', label: 'T-Cell' },
              { id: 'Combined', label: 'Combined' },
              { id: 'Phagocytic', label: 'Phagocytic' },
              { id: 'Complement', label: 'Complement' }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setLearnSection(tab.id)}
                className={`px-4 py-2 rounded-lg font-medium transition-all ${
                  learnSection === tab.id
                    ? 'bg-indigo-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {learnSection === 'overview' && (
             <div className="space-y-8">
                {/* --- FIXED DIAGRAM: LYMPHOCYTE DEVELOPMENT TREE --- */}
                <div className="bg-slate-50 border-2 border-slate-200 rounded-xl p-6 overflow-x-auto">
                  <h2 className="text-xl font-bold text-slate-800 mb-4 text-center">Lymphocyte Development & Associated Defects</h2>
                  <div className="flex flex-col items-center space-y-6 min-w-[700px]">
                    
                    {/* Level 1: Stem Cell */}
                    <div className="flex flex-col items-center">
                        <div className="p-3 bg-slate-200 rounded-lg font-bold border border-slate-400 w-48 text-center">Pluripotent Stem Cell</div>
                        <ArrowRight className="transform rotate-90 text-slate-400 my-1" />
                        <div className="p-3 bg-slate-200 rounded-lg font-bold border border-slate-400 w-48 text-center">Lymphoid Stem Cell</div>
                    </div>
                    
                    {/* Level 2: The Split */}
                    <div className="grid grid-cols-2 gap-12 w-full max-w-4xl">
                      
                      {/* Left Branch: B Cell */}
                      <div className="flex flex-col items-center border-t-2 border-slate-300 pt-4 relative">
                         <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-slate-50 px-2 text-slate-400 text-sm">Bone Marrow</div>
                         
                         <div className="flex items-center gap-2 mb-4">
                            <div className="p-3 bg-blue-100 border border-blue-300 rounded font-semibold w-40 text-center">Pro-B / Pre-B Cell</div>
                            <div className="bg-red-100 text-red-800 text-xs p-2 rounded border border-red-200 w-32">
                               🛑 <strong>Bruton's XLA</strong><br/>(BTK Defect)
                            </div>
                         </div>
                         
                         <ArrowRight className="transform rotate-90 text-slate-400 mb-4" />
                         
                         <div className="flex items-center gap-2 mb-4">
                            <div className="p-3 bg-blue-100 border border-blue-300 rounded font-semibold w-40 text-center">Immature B Cell</div>
                            <div className="bg-red-100 text-red-800 text-xs p-2 rounded border border-red-200 w-32">
                               🛑 <strong>CVID</strong><br/>(Differentiation)
                            </div>
                         </div>

                         <ArrowRight className="transform rotate-90 text-slate-400 mb-4" />

                         <div className="grid grid-cols-2 gap-4">
                            <div className="p-2 bg-blue-50 border border-blue-200 rounded text-center text-xs">IgM B-Cell</div>
                            <div className="flex flex-col items-center gap-1">
                                <div className="p-2 bg-blue-50 border border-blue-200 rounded text-center text-xs w-full">IgG/A/E B-Cell</div>
                                <div className="bg-red-100 text-red-800 text-[10px] p-1 rounded border border-red-200 w-full text-center">
                                   🛑 <strong>Hyper-IgM</strong>
                                </div>
                            </div>
                         </div>
                      </div>

                      {/* Right Branch: T Cell */}
                      <div className="flex flex-col items-center border-t-2 border-slate-300 pt-4 relative">
                         <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-slate-50 px-2 text-slate-400 text-sm">Thymus</div>

                         <div className="flex items-center gap-2 mb-4 flex-row-reverse">
                            <div className="p-3 bg-purple-100 border border-purple-300 rounded font-semibold w-40 text-center">Pro-T Cell</div>
                            <div className="bg-red-100 text-red-800 text-xs p-2 rounded border border-red-200 w-32">
                               🛑 <strong>SCID</strong><br/>(ADA / IL-2R)
                            </div>
                         </div>

                         <ArrowRight className="transform rotate-90 text-slate-400 mb-4" />

                         <div className="flex items-center gap-2 mb-4 flex-row-reverse">
                            <div className="p-3 bg-purple-100 border border-purple-300 rounded font-semibold w-40 text-center">Immature T Cell</div>
                            <div className="bg-red-100 text-red-800 text-xs p-2 rounded border border-red-200 w-32">
                               🛑 <strong>DiGeorge</strong><br/>(No Thymus)
                            </div>
                         </div>

                         <ArrowRight className="transform rotate-90 text-slate-400 mb-4" />

                         <div className="grid grid-cols-2 gap-4">
                            <div className="p-2 bg-purple-50 border border-purple-200 rounded text-center text-xs">CD4+ Helper</div>
                            <div className="p-2 bg-purple-50 border border-purple-200 rounded text-center text-xs">CD8+ Killer</div>
                         </div>
                      </div>
                    </div>
                  </div>
                </div>

               {/* STEP 1: INNATE */}
               <div className="bg-white rounded-xl shadow p-6 border-l-4 border-blue-500">
                 <h2 className="text-xl font-bold text-blue-900 mb-2">🧩 1. First Line of Defense: Innate Immunity (0–4 Hours)</h2>
                 <p className="text-gray-700 mb-2">When a pathogen enters the body:</p>
                 <ul className="list-disc ml-6 space-y-1 text-gray-700">
                    <li><strong>Pathogen has PAMPs</strong> (e.g., LPS, dsRNA, mannose)</li>
                    <li><strong>Host cells have PRRs</strong> (e.g., Toll-like receptors)</li>
                 </ul>
                 <div className="mt-3 bg-blue-50 p-3 rounded">
                    <p className="font-bold text-blue-800">Interaction Triggers:</p>
                    <ul className="list-disc ml-6 mt-1 text-sm text-blue-900">
                        <li><strong>Complement Activation:</strong> C3a/C4a/C5a (Inflammation), C3b (Opsonization), MAC (Lysis).</li>
                        <li><strong>Reaction:</strong> Happens immediately (minutes to hours).</li>
                    </ul>
                 </div>
               </div>

               {/* STEP 2: NK CELLS */}
               <div className="bg-white rounded-xl shadow p-6 border-l-4 border-indigo-500">
                 <h2 className="text-xl font-bold text-indigo-900 mb-2">🧩 2. Natural Killer (NK) Cells</h2>
                 <p className="text-gray-700">Part of innate immunity. They kill cells that either:</p>
                 <ul className="list-disc ml-6 mt-2 text-gray-700">
                    <li>Lack MHC I (virus-infected or tumor cells)</li>
                    <li>Express distress ligands</li>
                 </ul>
                 <p className="text-sm text-gray-600 mt-2">Mechanism: Apoptosis via perforin + granzymes.</p>
               </div>

               {/* STEP 3: CYTOKINE STORM */}
               <div className="bg-white rounded-xl shadow p-6 border-l-4 border-red-500">
                 <h2 className="text-xl font-bold text-red-900 mb-2">🧩 3. NF-κB Activation → Cytokine Storm</h2>
                 <p className="text-gray-700">PRR binding activates NF-κB, releasing pro-inflammatory cytokines:</p>
                 <div className="grid grid-cols-2 gap-4 mt-3">
                    <div className="bg-red-50 p-3 rounded">
                        <p className="font-bold text-red-800">Key Cytokines:</p>
                        <p>IL-1, IL-6 (Fever)</p>
                        <p>IL-8 (Chemotaxis)</p>
                        <p>IL-12 (T-cell differentiation)</p>
                        <p>TNF-α (Vascular permeability)</p>
                    </div>
                    <div className="bg-red-50 p-3 rounded">
                        <p className="font-bold text-red-800">Effects:</p>
                        <p>Fever, Swelling, Redness, Neutrophil recruitment.</p>
                    </div>
                 </div>
               </div>

               {/* STEP 4: INFLAMMATION */}
               <div className="bg-white rounded-xl shadow p-6 border-l-4 border-orange-500">
                 <h2 className="text-xl font-bold text-orange-900 mb-2">🧩 4. Inflammation (4–96 hours)</h2>
                 <p className="text-gray-700">Neutrophils arrive first. They kill via ROS (Respiratory Burst), Degranulation, and NETs.</p>
               </div>

               {/* STEP 5: DENDRITIC CELLS */}
               <div className="bg-white rounded-xl shadow p-6 border-l-4 border-purple-500">
                 <h2 className="text-xl font-bold text-purple-900 mb-2">🧩 5. Dendritic Cells: The Bridge</h2>
                 <p className="text-gray-700">Ingest pathogens and migrate to lymph nodes to present antigen.</p>
                 <ul className="list-disc ml-6 mt-2 text-gray-700">
                    <li><strong>MHC II:</strong> Activates CD4 T cells</li>
                    <li><strong>MHC I:</strong> Activates CD8 T cells (cross-presentation)</li>
                    <li><strong>B7 (CD80/86):</strong> Binds CD28 (Co-stimulation)</li>
                 </ul>
               </div>

               {/* STEP 6: T CELL ACTIVATION */}
               <div className="bg-white rounded-xl shadow p-6 border-l-4 border-teal-500">
                 <h2 className="text-xl font-bold text-teal-900 mb-2">🧩 6. T-Cell Activation</h2>
                 <div className="grid md:grid-cols-2 gap-4">
                    <div className="bg-teal-50 p-3 rounded">
                        <h3 className="font-bold text-teal-800">Th1 Cells (Cell-Mediated)</h3>
                        <p className="text-sm">Driven by IL-12. Secrete IFN-γ. Activate Macrophages + CD8. Target: Viruses, Intracellular bacteria.</p>
                    </div>
                    <div className="bg-teal-50 p-3 rounded">
                        <h3 className="font-bold text-teal-800">Th2 Cells (Humoral)</h3>
                        <p className="text-sm">Driven by IL-4. Secrete IL-4, IL-5, IL-13. Activate B Cells. Target: Parasites, Allergies.</p>
                    </div>
                 </div>
               </div>

               {/* STEP 8: B CELL ACTIVATION */}
               <div className="bg-white rounded-xl shadow p-6 border-l-4 border-pink-500">
                 <h2 className="text-xl font-bold text-pink-900 mb-2">🧩 8. B-Cell Activation & Antibodies</h2>
                 <p className="text-gray-700">Requires BCR binding (Signal 1) + Th2 CD40L binding (Signal 2). Differentiates into Plasma Cells.</p>
                 <div className="mt-3 bg-pink-50 p-3 rounded">
                    <p className="font-bold text-pink-800">Antibody Functions:</p>
                    <ul className="list-disc ml-6 text-sm">
                        <li>Neutralize toxins</li>
                        <li>Opsonize pathogens</li>
                        <li>Activate complement</li>
                    </ul>
                 </div>
               </div>

               {/* Pattern Recognition Framework */}
                <div className="bg-yellow-50 border-l-4 border-yellow-500 p-4 rounded">
                  <h3 className="font-bold text-lg text-yellow-900 mb-3">Clinical Pattern Recognition Framework</h3>
                  <div className="grid md:grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="font-bold text-yellow-900 mb-2">Age of Onset:</p>
                      <p className="text-yellow-800">• <strong>&lt;6 months:</strong> Severe (SCID, ADA deficiency) - no maternal antibody protection for cellular immunity</p>
                      <p className="text-yellow-800">• <strong>&gt;6 months:</strong> B-cell defects (XLA, CVID) - maternal IgG wanes around 6 months</p>
                      <p className="text-yellow-800">• <strong>Teens/20s:</strong> CVID often presents late</p>
                    </div>
                    <div>
                      <p className="font-bold text-yellow-900 mb-2">Infection Type → Defect:</p>
                      <p className="text-yellow-800">• <strong>Encapsulated bacteria:</strong> B-cell/antibody deficiency</p>
                      <p className="text-yellow-800">• <strong>Viral/fungal/opportunistic:</strong> T-cell deficiency</p>
                      <p className="text-yellow-800">• <strong>Catalase+ organisms/abscesses:</strong> Phagocyte defect</p>
                      <p className="text-yellow-800">• <strong>Neisseria:</strong> Complement deficiency</p>
                    </div>
                  </div>
                </div>

                {/* Quick Reference Table */}
                <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded overflow-x-auto">
                  <h2 className="text-2xl font-bold text-red-900 mb-4">Quick Reference: Infection Patterns</h2>
                  <table className="w-full text-sm">
                    <thead className="bg-red-100">
                      <tr>
                        <th className="p-3 text-left font-bold text-red-900">Immune Defect</th>
                        <th className="p-3 text-left font-bold text-red-900">Typical Organisms</th>
                        <th className="p-3 text-left font-bold text-red-900">Clinical Presentation</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-red-200 bg-white">
                      <tr>
                        <td className="p-3 font-medium">B-cell / Antibody</td>
                        <td className="p-3">S. pneumoniae, H. influenzae, S. aureus, Giardia</td>
                        <td className="p-3">Recurrent sinopulmonary infections, otitis media</td>
                      </tr>
                      <tr className="bg-red-50">
                        <td className="p-3 font-medium">T-cell</td>
                        <td className="p-3">PJP, CMV, Candida, Cryptococcus, VZV, mycobacteria</td>
                        <td className="p-3">Persistent thrush, viral infections, opportunistic infections</td>
                      </tr>
                      <tr>
                        <td className="p-3 font-medium">Phagocyte</td>
                        <td className="p-3">Catalase+: S. aureus, Aspergillus, Serratia, Nocardia, Burkholderia</td>
                        <td className="p-3">Recurrent abscesses, pneumonia, granulomas</td>
                      </tr>
                      <tr className="bg-red-50">
                        <td className="p-3 font-medium">Complement</td>
                        <td className="p-3">Neisseria (meningitidis, gonorrhoeae)</td>
                        <td className="p-3">Recurrent Neisseria infections, SLE-like symptoms</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
             </div>
          )}

          {learnSection !== 'overview' && (
            <div className="space-y-6">
              {diseaseDetails
                .filter(cat => cat.category.includes(learnSection))
                .map((category, idx) => (
                  <div key={idx}>
                    <h2 className="text-2xl font-bold text-indigo-900 mb-4">{category.category}</h2>
                    <div className="grid gap-4">
                      {category.diseases.map((d, i) => (
                        <div key={i} className="bg-white border-l-4 border-indigo-500 shadow-lg p-6 rounded-r-xl">
                          <div className="flex justify-between items-start mb-2">
                            <h3 className="font-bold text-xl text-gray-900">{d.name}</h3>
                            <span className="bg-indigo-100 text-indigo-800 text-xs px-2 py-1 rounded font-bold">{d.inheritance}</span>
                          </div>
                          <div className="grid md:grid-cols-2 gap-4 text-sm">
                            <div>
                                <p className="text-gray-600 mb-1"><strong>Gene/Defect:</strong> {d.gene} / {d.defect}</p>
                                <p className="text-gray-600 mb-1"><strong>Onset:</strong> {d.onset}</p>
                            </div>
                            <div>
                                <p className="text-gray-600 mb-1"><strong>Labs:</strong> {d.labs}</p>
                                <p className="text-gray-600 mb-1"><strong>Treatment:</strong> {d.treatment}</p>
                            </div>
                          </div>
                          <div className="mt-3 bg-gray-50 p-3 rounded border border-gray-200">
                             <p className="text-indigo-800 font-medium">🔑 Key Features: {d.key}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
            </div>
          )}
        </div>
        {renderFooter()}
      </div>
    </div>
  );

  const renderGame = () => {
    const isReverse = mode === 'reverse';
    const totalQuestions = activeQuestions.length;
    const questionData = activeQuestions[currentCase];

    if (gameComplete) {
      return (
        <div className="min-h-screen bg-gradient-to-br from-purple-50 to-pink-100 p-8 flex items-center justify-center">
           <div className="bg-white rounded-2xl shadow-2xl p-12 max-w-2xl text-center">
             <Trophy className="w-24 h-24 text-yellow-500 mx-auto mb-6" />
             <h1 className="text-4xl font-bold text-gray-800 mb-2">Congratulations!</h1>
             <p className="text-xl text-gray-600 mb-8">{isReverse ? 'Management Scenarios' : 'Clinical Cases'} Complete</p>
             
             <div className="bg-indigo-50 rounded-xl p-6 mb-8 border border-indigo-100">
               <p className="text-lg text-indigo-900 mb-2">Final Score</p>
               <p className="text-5xl font-bold text-indigo-600">{score} / {totalQuestions}</p>
               <p className="text-sm text-indigo-400 mt-2">{Math.round((score/totalQuestions)*100)}% Correct</p>
             </div>

             <button onClick={() => setMode('menu')} className="bg-indigo-600 text-white px-8 py-3 rounded-lg font-bold hover:bg-indigo-700 transition-colors">Back to Menu</button>
           </div>
        </div>
      );
    }
    
    if (!questionData) return <div>Loading...</div>;

    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 to-purple-100 p-4 md:p-8">
        <div className="max-w-4xl mx-auto">
          {/* Header Bar */}
          <div className="flex justify-between items-center mb-6">
            <button onClick={() => setMode('menu')} className="px-4 py-2 bg-white rounded-lg shadow hover:bg-gray-50 transition-colors text-gray-700">← Menu</button>
            <div className="flex items-center gap-4">
               <div className="bg-white px-4 py-2 rounded-lg shadow text-indigo-600 font-bold border border-indigo-100">
                 Score: {score} / {currentCase}
               </div>
               <div className="text-sm font-medium text-gray-500">Case {currentCase + 1} of {totalQuestions}</div>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
            <div className={`p-6 ${isReverse ? 'bg-pink-600' : 'bg-indigo-600'}`}>
              <h2 className="text-2xl font-bold text-white">
                {isReverse ? "Management Scenario" : questionData.title}
              </h2>
              {isReverse && <p className="text-pink-100 mt-1">{questionData.diagnosis}</p>}
              {!isReverse && <p className="text-indigo-100 mt-1">{questionData.age}</p>}
            </div>

            <div className="p-6 md:p-8">
               {isReverse ? (
                 <div className="mb-8">
                   <div className="bg-pink-50 p-6 rounded-xl border-l-4 border-pink-500 mb-6">
                      <p className="text-xl text-gray-800 font-medium leading-relaxed">{questionData.question}</p>
                   </div>
                 </div>
               ) : (
                 <div className="space-y-4 mb-8">
                   <div className="bg-blue-50 p-4 rounded-lg border-l-4 border-blue-500">
                     <p className="font-bold text-blue-900 mb-1">Presentation & History</p>
                     <p className="text-gray-800 leading-relaxed">{questionData.presentation}</p>
                   </div>
                   <div className="bg-purple-50 p-4 rounded-lg border-l-4 border-purple-500">
                     <p className="font-bold text-purple-900 mb-1">Physical Exam & Labs</p>
                     <p className="text-gray-800 leading-relaxed">{questionData.physicalExam} {questionData.labs}</p>
                   </div>
                   <div className="flex flex-wrap gap-2 mt-2">
                     {questionData.buzzwords?.map((word, i) => (
                       <span key={i} className="bg-yellow-100 text-yellow-800 px-3 py-1 rounded-full text-sm font-medium border border-yellow-200">{word}</span>
                     ))}
                   </div>
                 </div>
               )}

               <div className="space-y-3">
                 {questionData.options.map((opt, i) => (
                   <button
                     key={i}
                     onClick={() => handleAnswer(i)}
                     disabled={showExplanation}
                     className={`w-full p-4 rounded-xl text-left border-2 transition-all ${
                       !showExplanation 
                         ? 'border-gray-100 hover:border-indigo-200 hover:bg-gray-50' 
                         : opt.correct 
                           ? 'bg-green-100 border-green-500' 
                           : selectedAnswer === i ? 'bg-red-100 border-red-500' : 'opacity-50'
                     }`}
                   >
                     <div className="flex justify-between">
                       <span className="font-medium">{opt.text}</span>
                       {showExplanation && opt.correct && <CheckCircle className="text-green-600"/>}
                       {showExplanation && !opt.correct && selectedAnswer === i && <XCircle className="text-red-600"/>}
                     </div>
                   </button>
                 ))}
               </div>

               {showExplanation && (
                 <div className="mt-8 bg-gray-50 p-6 rounded-xl border border-gray-200 animate-fadeIn">
                   <h3 className="font-bold text-gray-900 mb-2">Explanation</h3>
                   <p className="text-gray-700 mb-4">{questionData.explanation}</p>
                   {!isReverse && (
                     <div className="grid grid-cols-2 gap-4 text-sm mb-4">
                       <div className="bg-white p-3 rounded border"><strong>Defect:</strong> {questionData.defect}</div>
                       <div className="bg-white p-3 rounded border"><strong>Treatment:</strong> {questionData.treatment}</div>
                     </div>
                   )}
                   <button onClick={nextCase} className="mt-2 w-full bg-indigo-600 text-white py-3 rounded-lg font-bold hover:bg-indigo-700 transition-colors">
                     {currentCase < totalQuestions - 1 ? "Next Question" : "Finish Set"}
                   </button>
                 </div>
               )}
            </div>
          </div>
          {renderFooter()}
        </div>
      </div>
    );
  };

  return (
    <>
      {mode === 'menu' && renderMenu()}
      {mode === 'learn' && renderLearnMode()}
      {(mode === 'play' || mode === 'reverse') && renderGame()}
    </>
  );
};

export default ImmunoDx;